<template>
  <div>
    <v-autocomplete
      v-if="showCategoryFilter"
      v-model="localCategoryId"
      :items="categoryOptions"
      :loading="isLoadingCategories"
      item-text="label"
      item-value="id"
      :label="$t('STOCK.PRODUCTS.CATEGORY')"
      clearable
      dense
      outlined
      hide-details
      class="mb-3"
      @change="handleCategoryChange"
    ></v-autocomplete>
    <v-autocomplete
      :value="value"
      :items="productOptions"
      :loading="isLoading"
      :label="label"
      item-text="label"
      item-value="id"
      clearable
      hide-details
      dense
      outlined
      cache-items
      :search-input.sync="search"
      @input="$emit('input', $event)"
      @update:search-input="handleSearch"
    >
      <template v-slot:item="{ item }">
        <v-list-item-content>
          <v-list-item-title>{{ item.display_name || item.label }}</v-list-item-title>
          <v-list-item-subtitle>
            {{ productSubtitle(item) }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { debounce } from "lodash";
import {
  GET_STOCK_PRODUCT,
  GET_STOCK_PRODUCT_OPTIONS,
} from "@/store/stock/stockProducts.module";
import { GET_STOCK_PRODUCT_CATEGORY_OPTIONS } from "@/store/stock/stockProductCategories.module";

export default {
  name: "StockProductPicker",
  props: {
    value: {
      default: null,
    },
    label: {
      type: String,
      default: "Product",
    },
    showCategoryFilter: {
      type: Boolean,
      default: true,
    },
    categoryId: {
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      isLoadingCategories: false,
      search: "",
      productOptions: [],
      categoryOptions: [],
      localCategoryId: this.categoryId,
      selectedProductCache: null,
      suppressSearch: false,
    };
  },
  watch: {
    categoryId(value) {
      this.localCategoryId = value;
      this.loadProducts(this.search || "");
    },
    value() {
      this.mergeSelectedProduct();
    },
  },
  mounted() {
    if (this.showCategoryFilter) {
      this.loadCategories();
    }
    this.loadProducts("");
  },
  methods: {
    productSubtitle(item) {
      const parts = [
        item.stock_product_category ? item.stock_product_category.label : null,
        item.tags_subtitle || item.code || null,
      ].filter(Boolean);

      return parts.length ? parts.join(" · ") : "-";
    },
    sameId(left, right) {
      if (left == null || right == null) {
        return false;
      }
      return Number(left) === Number(right);
    },
    isSelectedProductText(search) {
      if (search == null || !this.value) {
        return false;
      }

      const selected = this.selectedProductCache
        && this.sameId(this.selectedProductCache.id, this.value)
        ? this.selectedProductCache
        : this.productOptions.find((product) => this.sameId(product.id, this.value));

      if (!selected) {
        return false;
      }

      return [selected.label, selected.display_name, selected.code]
        .filter(Boolean)
        .some((text) => String(text) === String(search));
    },
    handleCategoryChange() {
      this.$emit("update:categoryId", this.localCategoryId);
      this.loadProducts(this.search || "");
    },
    loadCategories() {
      this.isLoadingCategories = true;
      return this.$store
        .dispatch(GET_STOCK_PRODUCT_CATEGORY_OPTIONS, {})
        .then((response) => {
          this.categoryOptions = response.data || [];
        })
        .finally(() => {
          this.isLoadingCategories = false;
        });
    },
    handleSearch(search) {
      if (this.suppressSearch || search == null || this.isSelectedProductText(search)) {
        return;
      }

      this.debouncedLoadProducts(search);
    },
    debouncedLoadProducts: debounce(function (search) {
      if (this.suppressSearch || this.isSelectedProductText(search)) {
        return;
      }
      this.loadProducts(search || "");
    }, 350),
    ensureOption(product) {
      if (!product || !product.id) {
        return;
      }

      this.suppressSearch = true;
      if (this.debouncedLoadProducts.cancel) {
        this.debouncedLoadProducts.cancel();
      }
      this.selectedProductCache = {
        ...product,
        id: Number(product.id),
        label: product.label || product.display_name || product.code || String(product.id),
      };
      this.mergeSelectedProduct();
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.suppressSearch = false;
        });
      });
    },
    withSelectedProduct(items) {
      const list = [...(items || [])];
      const selectedId = this.value;
      if (!selectedId) {
        return list;
      }

      if (list.some((product) => this.sameId(product.id, selectedId))) {
        return list;
      }

      if (this.selectedProductCache && this.sameId(this.selectedProductCache.id, selectedId)) {
        return [this.selectedProductCache, ...list];
      }

      return list;
    },
    mergeSelectedProduct() {
      const selectedId = this.value;
      if (!selectedId) {
        return;
      }

      if (this.productOptions.some((product) => this.sameId(product.id, selectedId))) {
        return;
      }

      if (this.selectedProductCache && this.sameId(this.selectedProductCache.id, selectedId)) {
        this.productOptions = [this.selectedProductCache, ...this.productOptions];
        return;
      }

      this.$store.dispatch(GET_STOCK_PRODUCT, selectedId).then((product) => {
        this.selectedProductCache = product;
        if (
          this.sameId(this.value, selectedId)
          && !this.productOptions.some((option) => this.sameId(option.id, product.id))
        ) {
          this.productOptions = [product, ...this.productOptions];
        }
      }).catch(() => {});
    },
    loadProducts(search) {
      this.isLoading = true;
      const params = { search };
      if (this.localCategoryId) {
        params.filters = {
          stock_product_category_id: [this.localCategoryId],
        };
      }
      return this.$store
        .dispatch(GET_STOCK_PRODUCT_OPTIONS, params)
        .then((response) => {
          this.productOptions = this.withSelectedProduct(response.data || []);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
